import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Select, Space, Tag, message, Popconfirm, Modal, Form, DatePicker } from 'antd';
import { ReloadOutlined, DeleteOutlined, EditOutlined, StopOutlined, SearchOutlined, CopyOutlined } from '@ant-design/icons';
import siteConfig from '../../../config/site-config';
import dayjs from 'dayjs';

const { Search } = Input;

function formatDate(dateString) {
  if (!dateString) return '-';
  return dayjs(dateString).format('YYYY-MM-DD HH:mm:ss');
}

function getStatusTag(token) {
  const now = new Date();
  const expiresAt = new Date(token.expires_at);
  
  if (token.status === 'revoked') {
    return <Tag color="red">已撤销</Tag>;
  }
  if (token.status === 'expired' || expiresAt <= now) {
    return <Tag color="default">已过期</Tag>;
  }
  if (token.status === 'active' && expiresAt > now) {
    return <Tag color="green">有效</Tag>;
  }
  return <Tag>{token.status}</Tag>;
}

/**
 * 令牌管理表格组件
 * 权限：令牌查看（只读） / 令牌管理（可编辑删除）
 */
export function TokenManagementTable({ canManage = false }) {
  const [loading, setLoading] = useState(false);
  const [tokens, setTokens] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [filters, setFilters] = useState({
    status: 'all',
    keyword: ''
  });
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingToken, setEditingToken] = useState(null);
  const [form] = Form.useForm();

  // 加载令牌列表
  const loadTokens = async (page = 1, pageSize = 20, currentFilters = filters) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
        status: currentFilters.status,
        keyword: currentFilters.keyword,
        fold_by: 'uid'
      });
      
      const response = await fetch(`${siteConfig.getApiUrl()}/tracker/tokens/admin-list?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setTokens(data.tokens || []);
        setPagination({
          current: data.pagination.page,
          pageSize: data.pagination.pageSize,
          total: data.pagination.total
        });
      } else {
        message.error(data.error || '加载令牌列表失败');
      }
    } catch (error) {
      console.error('加载令牌列表失败:', error);
      message.error('加载令牌列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 撤销令牌
  const handleRevoke = async (tokenStr) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${siteConfig.getApiUrl()}/tracker/tokens/${encodeURIComponent(tokenStr)}/revoke`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        message.success('令牌已撤销');
        loadTokens(pagination.current, pagination.pageSize);
      } else {
        message.error(data.error || '撤销令牌失败');
      }
    } catch (error) {
      console.error('撤销令牌失败:', error);
      message.error('撤销令牌失败');
    }
  };

  // 删除令牌
  const handleDelete = async (tokenStr) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${siteConfig.getApiUrl()}/tracker/tokens/${encodeURIComponent(tokenStr)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        message.success('令牌已删除');
        loadTokens(pagination.current, pagination.pageSize);
      } else {
        message.error(data.error || '删除令牌失败');
      }
    } catch (error) {
      console.error('删除令牌失败:', error);
      message.error('删除令牌失败');
    }
  };

  // 清理无设备行的孤儿 active 令牌
  const handleCleanupOrphans = async () => {
    try {
      const token = localStorage.getItem('token');
      const previewRes = await fetch(`${siteConfig.getApiUrl()}/tracker/tokens/orphan-preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const preview = await previewRes.json().catch(() => ({}));
      if (!previewRes.ok || !preview.success) {
        throw new Error(preview.error || '预览孤儿令牌失败');
      }
      const count = Number(preview.count || 0);
      if (!count) {
        message.info('没有可清理的孤儿令牌');
        return;
      }
      const ok = window.confirm(`发现 ${count} 条无对应设备的有效令牌（同名多行常见来源）。确认撤销它们？`);
      if (!ok) return;
      const response = await fetch(`${siteConfig.getApiUrl()}/tracker/tokens/cleanup-orphans`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ mode: 'revoke' })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data.error || '清理失败');
      }
      message.success(`已撤销 ${data.affected || 0} 条孤儿令牌`);
      loadTokens(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error('清理孤儿令牌失败:', error);
      message.error(error.message || '清理孤儿令牌失败');
    }
  };

  // 编辑令牌
  const handleEdit = (token) => {
    setEditingToken(token);
    form.setFieldsValue({
      note: token.note || '',
      expires_at: token.expires_at ? dayjs(token.expires_at) : null
    });
    setEditModalVisible(true);
  };

  // 保存编辑
  const handleSaveEdit = async () => {
    try {
      const values = await form.validateFields();
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${siteConfig.getApiUrl()}/tracker/tokens/${encodeURIComponent(editingToken.token)}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          note: values.note,
          expires_at: values.expires_at ? values.expires_at.toISOString() : null
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        message.success('令牌信息已更新');
        setEditModalVisible(false);
        setEditingToken(null);
        form.resetFields();
        loadTokens(pagination.current, pagination.pageSize);
      } else {
        message.error(data.error || '更新令牌信息失败');
      }
    } catch (error) {
      console.error('更新令牌信息失败:', error);
      message.error('更新令牌信息失败');
    }
  };

  // 搜索
  const handleSearch = (keyword) => {
    const newFilters = { ...filters, keyword };
    setFilters(newFilters);
    loadTokens(1, pagination.pageSize, newFilters);
  };

  // 状态筛选
  const handleStatusChange = (status) => {
    const newFilters = { ...filters, status };
    setFilters(newFilters);
    loadTokens(1, pagination.pageSize, newFilters);
  };

  // 表格分页变化
  const handleTableChange = (paginationChange) => {
    loadTokens(paginationChange.current, paginationChange.pageSize);
  };

  // 初始加载
  useEffect(() => {
    loadTokens();
  }, []);

  const columns = [
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      width: 90,
      fixed: 'left'
    },
    {
      title: '小组',
      dataIndex: 'group_name',
      key: 'group_name',
      width: 90
    },
    {
      title: '组织',
      dataIndex: 'org_name',
      key: 'org_name',
      width: 110
    },
    {
      title: '令牌数',
      dataIndex: 'token_count',
      key: 'token_count',
      width: 72,
      render: (v, record) => Number(v || record.tokens?.length || 1)
    },
    {
      title: '设备',
      key: 'orphan',
      width: 88,
      render: (_, record) => (
        record.is_orphan
          ? <Tag color="orange">无设备</Tag>
          : <Tag color="green">有设备</Tag>
      )
    },
    {
      title: '设备ID',
      dataIndex: 'device_unique_id',
      key: 'device_unique_id',
      width: 140,
      ellipsis: true
    },
    {
      title: '推流码',
      key: 'stream_key',
      width: 160,
      render: (_, record) => {
        const sk = String(record.stream_key || record.token || '').trim();
        if (!sk) {
          return canManage ? (
            <Button
              type="link"
              size="small"
              onClick={async () => {
                try {
                  const auth = localStorage.getItem('token');
                  const response = await fetch(`${siteConfig.getApiUrl()}/tracker/assign-short-token`, {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${auth}`,
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                      device_unique_id: record.device_unique_id || undefined,
                      name: record.name || undefined,
                      force: false
                    })
                  });
                  const data = await response.json().catch(() => ({}));
                  if (!response.ok || !data.success) {
                    throw new Error(data.error || '获取推流码失败');
                  }
                  const next = String(data.stream_key || data.token?.token || '').trim();
                  if (next) {
                    try {
                      await navigator.clipboard.writeText(next);
                      message.success(`已获取并复制：${next}`);
                    } catch (_) {
                      message.success(`已获取：${next}`);
                    }
                  } else {
                    message.success('已获取推流码');
                  }
                  loadTokens(pagination.current, pagination.pageSize);
                } catch (err) {
                  message.error(err.message || '获取推流码失败');
                }
              }}
            >
              获取
            </Button>
          ) : '-';
        }
        return (
          <Space size={4}>
            <span style={{ fontFamily: 'monospace', fontSize: 12 }} title={sk}>{sk.length > 18 ? sk.slice(0, 16) + '…' : sk}</span>
            <Button
              type="link"
              size="small"
              icon={<CopyOutlined />}
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(sk);
                  message.success('已复制推流码');
                } catch (_) {
                  message.error('复制失败');
                }
              }}
            />
            {canManage ? (
              <Button
                type="link"
                size="small"
                onClick={async () => {
                  try {
                    const auth = localStorage.getItem('token');
                    const body = {
                      device_unique_id: record.device_unique_id || undefined,
                      name: record.name || undefined,
                      force: false
                    };
                    const doAssign = async (force) => {
                      const response = await fetch(`${siteConfig.getApiUrl()}/tracker/assign-short-token`, {
                        method: 'POST',
                        headers: {
                          'Authorization': `Bearer ${auth}`,
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({ ...body, force })
                      });
                      const data = await response.json().catch(() => ({}));
                      if (response.status === 409 && data.code === 'needs_confirm') {
                        const ok = window.confirm(data.error || '该队员已有推流码，重新获取将作废旧码，是否继续？');
                        if (!ok) return { cancelled: true };
                        return doAssign(true);
                      }
                      if (!response.ok || !data.success) {
                        throw new Error(data.error || '获取推流码失败');
                      }
                      return data;
                    };
                    const data = await doAssign(false);
                    if (data?.cancelled) return;
                    const next = String(data.stream_key || data.token?.token || '').trim();
                    if (next) {
                      try {
                        await navigator.clipboard.writeText(next);
                        message.success(data.regenerated ? `已重新获取并复制：${next}` : `已获取并复制：${next}`);
                      } catch (_) {
                        message.success(data.regenerated ? `已重新获取：${next}` : `已获取：${next}`);
                      }
                    } else {
                      message.success('已获取推流码');
                    }
                    loadTokens(pagination.current, pagination.pageSize);
                  } catch (err) {
                    message.error(err.message || '获取推流码失败');
                  }
                }}
              >
                {sk ? '重新获取' : '获取'}
              </Button>
            ) : null}
          </Space>
        );
      }
    },
    {
      title: '状态',
      key: 'status',
      width: 72,
      render: (_, record) => getStatusTag(record)
    },
    {
      title: '绑定状态',
      key: 'bound',
      width: 80,
      render: (_, record) => (
        record.bound ? <Tag color="blue">已绑定</Tag> : <Tag>未绑定</Tag>
      )
    },
    {
      title: '备注',
      dataIndex: 'note',
      key: 'note',
      width: 100,
      ellipsis: true,
      render: (text) => text || '-'
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 136,
      render: formatDate
    },
    {
      title: '首次使用',
      dataIndex: 'first_used_at',
      key: 'first_used_at',
      width: 136,
      render: formatDate
    },
    {
      title: '最后使用',
      dataIndex: 'last_used_at',
      key: 'last_used_at',
      width: 136,
      render: formatDate
    },
    {
      title: '过期时间',
      dataIndex: 'expires_at',
      key: 'expires_at',
      width: 136,
      render: (text, record) => record.permanent ? <Tag color="gold">长期有效</Tag> : formatDate(text)
    }
  ];

  // 仅当有管理权限时显示操作列
  if (canManage) {
    columns.push({
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 168,
      render: (_, record) => (
        <Space size={0} wrap>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          {record.status === 'active' && (
            <Popconfirm
              title="确定要撤销此令牌吗？"
              onConfirm={() => handleRevoke(record.token)}
              okText="确定"
              cancelText="取消"
            >
              <Button
                type="link"
                size="small"
                danger
                icon={<StopOutlined />}
              >
                撤销
              </Button>
            </Popconfirm>
          )}
          <Popconfirm
            title="确定要删除此令牌吗？此操作不可恢复。"
            onConfirm={() => handleDelete(record.token)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    });
  }

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space>
          <Search
            placeholder="搜索名称、小组、组织、设备ID、备注"
            allowClear
            onSearch={handleSearch}
            style={{ width: 300 }}
            prefix={<SearchOutlined />}
          />
          <Select
            value={filters.status}
            onChange={handleStatusChange}
            style={{ width: 120 }}
            options={[
              { value: 'all', label: '全部状态' },
              { value: 'active', label: '有效' },
              { value: 'revoked', label: '已撤销' },
              { value: 'expired', label: '已过期' }
            ]}
          />
        </Space>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => loadTokens(pagination.current, pagination.pageSize)}
        >
          刷新
        </Button>
      </div>

      <Table
        size="small"
        columns={columns}
        dataSource={tokens}
        rowKey="token"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: 1280 }}
      />

      <Modal
        title="编辑令牌信息"
        open={editModalVisible}
        onOk={handleSaveEdit}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingToken(null);
          form.resetFields();
        }}
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item label="名称">
            <Input value={editingToken?.name} disabled />
          </Form.Item>
          <Form.Item label="小组">
            <Input value={editingToken?.group_name} disabled />
          </Form.Item>
          <Form.Item
            name="note"
            label="备注"
          >
            <Input.TextArea rows={3} placeholder="可选备注信息" />
          </Form.Item>
          <Form.Item
            name="expires_at"
            label="过期时间"
          >
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
